import T from 'prop-types';
import React, { Component } from 'react';
import MimeTypes from 'shared/helpers/mime-types';
import DropTarget from 'shared/components/file-drop-target';
import DataPendingPane from './panes/data-pending';
import AudioPane from './panes/audio-pane';
import MediaMissingPane from './panes/media-missing';
import PendingUploadPane from './panes/pending-upload';
import EncodingInProgess from './panes/encoding-in-progress';
import FixedRatioColoredBackground from '../fixed-ratio-colored-background';

const getType = ({ isDragOver, status }) => {
  if (isDragOver) {
    return 'info';
  }

  switch (status) {
    case 'error':
    case 'warning':
      return status;
    default:
      return undefined;
  }
};

class AudioFile extends Component {
  constructor(props, ...rest) {
    super(props, ...rest);

    this.state = {
      isDragOver: false,
    };
  }

  onDragOver = () => {
    if (!this.state.isDragOver) {
      this.setState({ isDragOver: true });
    }
  };

  onDragEnd = () => {
    this.setState({ isDragOver: false });
  };

  onDrop = file => {
    this.setState({ isDragOver: false });
    this.props.onFileReceived(file);
  };

  onFileNotAllowed = () => {
    this.setState({ isDragOver: false });
    this.props.onFileRejected();
  };

  renderAudioFile() {
    const { status, fileName, encodedDuration, sourceDuration, retry, onFileReceived } = this.props;
    const props = { fileName, retry, replace: onFileReceived };
    const dragOverType = this.state.isDragOver ? 'info' : undefined;

    return (
      <>
        {status === 'missing' && (
          <MediaMissingPane {...props} type={dragOverType} onFileSelected={onFileReceived} />
        )}
        {status === 'error' && (
          <AudioPane {...props} type={dragOverType || 'error'} duration={sourceDuration} />
        )}
        {status === 'warning' && (
          <AudioPane
            {...props}
            type={dragOverType || 'warning'}
            duration={sourceDuration}
            replace={undefined}
          />
        )}
        {status === 'ok' && (
          <AudioPane {...props} type={dragOverType} duration={encodedDuration} retry={undefined} />
        )}
      </>
    );
  }

  render() {
    const { isDragOver } = this.state;
    const { status, uploadProgress, fileName, sourceDuration, encodingStart } = this.props;

    return (
      <FixedRatioColoredBackground type={getType({ isDragOver, status })}>
        <DropTarget
          className="episode-audio-file"
          types={['Files']}
          allow={MimeTypes.Audio}
          onDragOver={this.onDragOver}
          onDragEnd={this.onDragEnd}
          onDrop={this.onDrop}
          onFileNotAllowed={this.onFileNotAllowed}
        >
          <div className="episode-audio-file__body">
            {status === 'initializing' && <DataPendingPane />}
            {status === 'uploading' && (
              <PendingUploadPane progress={uploadProgress} fileName={fileName} />
            )}
            {status === 'encoding' && (
              <EncodingInProgess
                fileName={fileName}
                duration={sourceDuration}
                start={encodingStart}
              />
            )}
            {(status === 'error' ||
              status === 'warning' ||
              status === 'ok' ||
              status === 'missing') &&
              this.renderAudioFile()}
          </div>
        </DropTarget>
      </FixedRatioColoredBackground>
    );
  }
}

AudioFile.propTypes = {
  status: T.oneOf(['ok', 'initializing', 'error', 'warning', 'missing', 'uploading', 'encoding']),
  fileName: T.string,
  encodedDuration: T.number,
  sourceDuration: T.number,
  encodingStart: T.string,
  uploadProgress: T.number,
  retry: T.func,
  onFileReceived: T.func,
  onFileRejected: T.func,
};

AudioFile.defaultProps = {
  status: 'ok',
  onFileReceived: () => {},
  onFileRejected: () => {},
};

export default AudioFile;
